import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { Col, Row } from 'react-bootstrap'
import { compose } from 'redux'
import useFeatureFlag from 'shared/hooks/useFeatureFlag/useFeatureFlag'
import { useCatalogCategoryProducts } from '../../api/hooks'
import { withActiveSortParams, withActiveProductFilters } from '../../reducers'
import Link from 'components/link'
import Image from 'components/image'
import CategoryHero from './category-hero'
import FacetsBar from '../Filters/components/FacetsBar/FacetsBar'
import ProductGrid, { mapHeroesToProductCards } from '../product-grid'
import { ProductFilters } from '../product-filters'
import ShopByGuides from '../shop-by-guides'
import FrequentlyAskedQuestions from '../FrequentlyAskedQuestions/FrequentlyAskedQuestions'
import { parseFilterQueryParams } from 'store/lib'
import { filterEmptyFilters } from '../product-filters/helpers'

export const HeroImage = ({ className, category }) => (
  <CategoryHero
    className={className}
    imageUrl={category?.teaser?.imageUrl}
    teaser={category?.teaser?.text}
    teaserLinkText={category?.teaserLinkText}
    teaserLinkUrl={category?.teaserLinkUrl}
    title={category.title}
  />
)

HeroImage.propTypes = {
  className: PropTypes.string,
  category: PropTypes.object.isRequired,
}

HeroImage.defaultProps = {
  className: '',
}

// export base so existing tests pass
export const LeafCategoryPageBase = ({
  category,
  location,
  sortParams,
  productFilters,
  router,
}) => {
  const deprecatedFilters = filterEmptyFilters(productFilters)
  const hasDeprecatedFilters = Object.keys(deprecatedFilters).length > 0
  const displayNewFacetsAndFilters = !hasDeprecatedFilters

  const [filtersToApply, setFiltersToApply] = useState(() =>
    displayNewFacetsAndFilters
      ? parseFilterQueryParams(location.search)
      : deprecatedFilters
  )
  const sortOption = new URLSearchParams(location.search).get('sort')

  useEffect(() => {
    const newFilters = displayNewFacetsAndFilters
      ? parseFilterQueryParams(location.search)
      : deprecatedFilters
    setFiltersToApply(newFilters)
  }, [location.search, productFilters])

  const handleClearFilters = () => {
    setFiltersToApply({})
    router.push(location.pathname)
  }

  const { data, isLoading } = useCatalogCategoryProducts(category.urlSlug, {
    heroes: true,
    filters: filtersToApply,
    sort: sortOption,
    orderBy: {
      key: sortParams?.sort,
      direction: sortParams?.order,
    },
    inFacetsFeature: displayNewFacetsAndFilters,
  })

  const productCards = isLoading
    ? null
    : data.heroProducts.map((p) => mapHeroesToProductCards(p))
  const heroProducts = isLoading ? null : data.heroProducts
  const facets = isLoading ? null : data.facets

  const teaserImageUrl = category?.teaser?.imageUrl
  const teaserText = category?.teaser?.text

  return (
    <div>
      <Row>
        <Col sm={teaserImageUrl || !category.isSortable ? 12 : 8} xs={12}>
          <HeroImage category={category} />
        </Col>
      </Row>
      {!teaserImageUrl && <hr className="mts mbm" />}
      {displayNewFacetsAndFilters && facets ? (
        <FacetsBar categorySlug={category.urlSlug} facets={facets} />
      ) : (
        heroProducts && <ProductFilters products={heroProducts} />
      )}
      {teaserText && !teaserImageUrl && (
        <div className="hidden-xs h6">{teaserText}</div>
      )}
      {productCards?.length === 0 && (
        <div className="well flex col paxl align-center">
          <div className="text-center text-muted pvxxl">
            <div
              className="mbm flex col align-center"
              style={{ maxWidth: 480 }}
            >
              <Image
                src="//images.babylist.com/image/upload/v1715118923/spilled_milk_asfyrb.svg"
                width={185}
              />
              <div className="mtl">
                Whoops! We couldn't find any matches that fit your current
                filters. Adjust your selections or{' '}
                <Link
                  className="text-underline link-info"
                  onClick={handleClearFilters}
                >
                  clear all filters
                </Link>{' '}
                to see all available products.
              </div>
            </div>
          </div>
        </div>
      )}
      <ProductGrid
        activeSlugSet={location?.state?.slugSet}
        guides={category.guides}
        productCards={productCards}
      />
      <ShopByGuides guides={category.guides} />
      {category.frequentlyAskedQuestions &&
        category.frequentlyAskedQuestions.length > 0 && (
          <div className="mtxl">
            <FrequentlyAskedQuestions
              headerVariant="secondary"
              questions={category.frequentlyAskedQuestions}
            />
          </div>
        )}
    </div>
  )
}

LeafCategoryPageBase.propTypes = {
  category: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  sortParams: PropTypes.object.isRequired,
  productFilters: PropTypes.object.isRequired,
}

export const LeafCategoryPage = compose(
  withActiveProductFilters(),
  withActiveSortParams()
)(withRouter(LeafCategoryPageBase))
